import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import useTimeline from '@src/hooks/useTimeline';
import useTimelineDrag from '@src/hooks/useTimelineDrag';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useStableCallback from '@src/hooks/useStableCallback';
import actions from '@src/redux/actions';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import Clip from '@src/new-components/Timeline/Clip';
import ClipSkeleton from '@src/new-components/Timeline/ClipSkeleton';
import ClipMenu from '@src/new-components/Timeline/ClipMenu';
import AppendButton from '@src/new-components/Timeline/AppendButton';
import InsertButton from '@src/new-components/Timeline/InsertButton';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import CommonButton from '@src/new-components/Button';
import Typography from '@src/new-components/Typography';
import { COLOR } from '@src/styles/color';
import {
  blocksToClips,
  resolveClipVariant,
  computeEdgeScrollVelocity,
  clampTrimEnd,
  clampTrimStart,
  clampMove,
  neighborBounds,
  snapSec,
  voiceoverLaneLayout,
} from '@src/services/timeline';
import {
  audioExpired,
  checkBlockTextError,
  validateBlockTimeChange,
} from '@src/services/project';
import { convertSecondsToSubtitleTime } from '@src/utils/time';
import { HISTORY_LABEL, HISTORY_SCOPE } from '@src/constants/history';
import {
  BLOCK_VARIANT,
  CLIP_MIN_DURATION_SEC,
  SNAP_THRESHOLD_PX,
  WAVEFORM_MIN_CLIP_PX,
} from '@src/constants/timeline';
import {
  BLOCK_STATUS,
  CONVERTING_STATUS,
  MAX_CHARACTER_PER_BLOCK_SRT,
  TRANSLATION_STATUS,
} from '@src/constants/project';
import { getMeasuredAudioDuration } from '@src/services/audioDuration';
import useBlockTrimFlags from './useBlockTrimFlags';
import useBlockWaveforms from './useBlockWaveforms';
import useClipReveal from './useClipReveal';
import { StyledAppendWrap, StyledDragTooltip, StyledLane } from './index.style';

const overlapsVisible = (clip, { fromSec, toSec }) =>
  clip.endSec >= fromSec && clip.startSec <= toSec;

// A gap narrower than this has no room for the insert button, so it gets none.
const INSERT_SLOT_MIN_PX = 40;

/**
 * Voice track lane — dubbing blocks from redux `multiBlock`, rendered as clips
 * positioned by their SRT timing. Only clips inside the visible window are
 * rendered, so a long project never mounts thousands of nodes. A selected clip
 * (the shared `currentBlockId`, synced with the block list) can be trimmed from
 * either edge or moved within its neighbours, with snapping and a time tooltip.
 *
 * While `isExtracting`, a non-interactive skeleton clip trails the real ones to
 * show that blocks are still arriving.
 *
 * `revealBlockId` is a one-shot request to scroll a block's clip into view (the
 * editor sets it when it jumps the block list to a block, e.g. right after
 * appending one) — see useClipReveal.
 */
const VoiceoverTrack = ({
  onAddBlock,
  onInsertBlock,
  isAddBlockDisabled = false,
  blackWordsList = [],
  isExtracting = false,
  revealBlockId = null,
  onSelectBlock,
  isLocked = false,
  onScrubTo,
  onScrubEnd,
  isPlaying = false,
}) => {
  const { t } = useTranslation();
  const {
    secToPx,
    pxToSec,
    visibleRange,
    viewportRef,
    scrollViewportBy,
    durationSec,
  } = useTimeline();
  const dispatch = useDispatch();
  const { getFeatureValue } = useFeatureFlags();
  const user = useSelector((state) => state.auth.user);
  const blocks = useSelector((state) => state.project.multiBlock.blocks);
  const blockIds = useSelector((state) => state.project.multiBlock.blockIds);
  const translationStatus = useSelector(
    (state) => state.project.translation?.status,
  );
  const voiceDefault = useSelector((state) => state.project.voiceDefault);
  const speakers = useSelector((state) => state.project.speakers);
  // Selection is the shared block focus (`currentBlockId`), so a clip and its
  // block-list row highlight together in both directions — and it follows
  // playback, matching how the list highlights the current block.
  const currentBlockId = useSelector((state) => state.project.currentBlockId);
  const [draftRange, setDraftRange] = useState(null); // { id, startSec, endSec }
  const [menu, setMenu] = useState(null); // { position: { top, left }, blockId }
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  // The lane's own right edge: the append button sits past the last clip (and
  // past the extraction skeleton), so it — not the last clip — is where this
  // track ends. useClipReveal needs that to reveal the last clip without
  // scrolling into the space belonging to a longer track.
  const appendRef = useRef(null);

  // With a successful translation active, clips label the translated text —
  // the text that will actually be synthesized — same as the block editor.
  const useTranslatedText = translationStatus === TRANSLATION_STATUS.SUCCESS;

  // Same text-error check the block editor highlights with, so a clip turns red
  // in every case the editor does (too long / invalid chars / blacklisted).
  // Voice is resolved EXACTLY like ContentBlock — block voice, else the block's
  // speaker voice (when speaker config is on), else the project default — so the
  // `isVbeeVoice` branch in checkBlockTextError matches and the flags agree.
  const getTextError = useCallback(
    (text, block) => {
      const speaker = speakers?.find((s) => s.id === block.speakerId);
      const voice = block.voice || speaker?.voice || voiceDefault;
      const { hasError } = checkBlockTextError(
        text,
        MAX_CHARACTER_PER_BLOCK_SRT,
        blackWordsList,
        voice,
      );
      return hasError;
    },
    [speakers, blackWordsList, voiceDefault],
  );

  // Map blocks → clips only when the data changes. Cheap now (status + timing
  // only); text validation runs per visible clip below.
  const clips = useMemo(
    () => blocksToClips(blocks, blockIds, { useTranslatedText }),
    [blocks, blockIds, useTranslatedText],
  );

  useClipReveal({ clips, revealBlockId, appendRef });

  // Window to the visible range (recomputes per bucket, not per scroll frame).
  const visibleClips = useMemo(
    () => clips.filter((clip) => overlapsVisible(clip, visibleRange)),
    [clips, visibleRange],
  );

  // A clip's PLAYABLE audio source, or null. Shared by trim detection and
  // waveform decoding so the rule stays in one place — and it's the SAME rule
  // the timeline player uses (`blockStatus === AUDIO_AVAILABLE`, see
  // useVideoDubbingAudioPlayer): success-synthesized, not changed (an edited
  // block is DRAFT), not error/in-progress, not expired. So a block that won't
  // play shows the "no audio yet" stripes instead of a stale waveform, matching
  // the block editor's Convert state.
  const audioSourceOf = useCallback(
    (clip) => {
      const block = blocks[clip.id];
      const audioLink = block?.audioLink;
      if (
        block?.blockStatus !== BLOCK_STATUS.AUDIO_AVAILABLE ||
        !audioLink ||
        audioExpired(block?.audioExpiredAt)
      ) {
        return null;
      }
      return {
        id: clip.id,
        audioLink,
        startTime: block.startTime,
        endTime: block.endTime,
      };
    },
    [blocks],
  );

  // Trim detection input — only the VISIBLE blocks that have live audio, so the
  // (cheap, metadata-only) reads scale with the viewport, not the whole project.
  const trimSources = useMemo(
    () => visibleClips.map(audioSourceOf).filter(Boolean),
    [visibleClips, audioSourceOf],
  );
  const trimmedIds = useBlockTrimFlags(trimSources);

  // Resolve each VISIBLE clip's final variant — the only place the expensive
  // text-validation (regex + blacklist) runs, so cost scales with what's on
  // screen, not the whole project. Text error wins over the trimmed look.
  const visibleVariants = useMemo(() => {
    const variants = {};
    visibleClips.forEach((clip) => {
      const block = blocks[clip.id];
      const hasTextError = block ? getTextError(clip.text, block) : false;
      variants[clip.id] = hasTextError
        ? BLOCK_VARIANT.ERROR
        : resolveClipVariant(clip.variant, trimmedIds.has(clip.id));
    });
    return variants;
  }, [visibleClips, blocks, getTextError, trimmedIds]);

  // A clip shows its audio waveform whenever the block has playable audio;
  // `audioIds` drives waveform-vs-stripes per clip.
  const audioIds = useMemo(
    () => new Set(trimSources.map((source) => source.id)),
    [trimSources],
  );

  // Decode only clips wide enough to render a legible waveform. This is what
  // stops the fitted-zoom first load of a large project from fetching + decoding
  // every block's audio (all visible but a few px each); the set widens as the
  // user zooms in. Decoding is far heavier than trim's metadata read, so it must
  // be gated tighter than `trimSources`.
  const waveformSources = useMemo(
    () =>
      visibleClips
        .filter(
          (clip) =>
            secToPx(clip.endSec) - secToPx(clip.startSec) >=
            WAVEFORM_MIN_CLIP_PX,
        )
        .map(audioSourceOf)
        .filter(Boolean),
    [visibleClips, audioSourceOf, secToPx],
  );
  const blockWaveforms = useBlockWaveforms(waveformSources);

  // The lane's tail (skeleton + append button) sits just past the last clip's
  // end. While the last clip is being dragged (trim/move), follow its live draft
  // end so the tail moves with it instead of staying pinned at the committed
  // position.
  const lastClip = clips.length ? clips[clips.length - 1] : null;
  const lastEndSec =
    lastClip && draftRange?.id === lastClip.id
      ? draftRange.endSec
      : lastClip?.endSec ?? 0;

  // While extraction streams blocks in, a placeholder clip trails the ones that
  // have arrived so the lane shows there is more coming — the block list does
  // the same with its skeleton rows. It takes the append button's slot and
  // pushes the button out behind it.
  const { skeletonLeft, appendLeft } = voiceoverLaneLayout({
    lastEndPx: secToPx(lastEndSec),
    showSkeleton: isExtracting,
  });

  // Stable so React.memo(Clip) holds across re-renders (e.g. during playback) —
  // which requires `onSelectBlock` to be stable too, or every clip re-renders on
  // each parent render.
  //
  // The editor owns what selecting a clip means: it writes the shared focus so
  // the block list highlights the block, scrolls the list to it, and — only while
  // nothing is playing — puts the caret in it.
  const handleSelect = useCallback(
    (id) => onSelectBlock?.(id),
    [onSelectBlock],
  );

  // Highlight only, without the scroll and caret that a left click brings — used
  // by the context menu, where jumping the block list and stealing the caret
  // would fight the menu the user just opened.
  const highlightBlock = useCallback(
    (id) => dispatch(actions.project.setCurrentBlockId(id)),
    [dispatch],
  );

  // Scroll-aware pointer→seconds: account for the viewport's left edge and its
  // current scrollLeft, so a drag delta stays correct even while auto-scrolling.
  const toSec = useCallback(
    (clientX) => {
      const el = viewportRef.current;
      if (!el) return 0;
      const rect = el.getBoundingClientRect();
      return pxToSec(clientX - rect.left + el.scrollLeft);
    },
    [viewportRef, pxToSec],
  );

  // Edge auto-scroll config for the drag hook — reuses the same velocity curve
  // as the playhead scrub so dragging a clip past the viewport edge reveals more.
  const autoScroll = useMemo(
    () => ({
      getVelocity: (clientX) => {
        const el = viewportRef.current;
        if (!el) return 0;
        const rect = el.getBoundingClientRect();
        return computeEdgeScrollVelocity(clientX, rect.left, rect.width);
      },
      scrollBy: (deltaPx) => scrollViewportBy(deltaPx),
    }),
    [viewportRef, scrollViewportBy],
  );

  const { beginDrag } = useTimelineDrag({ toSec, autoScroll });

  // Every clip edit is off while a download/export runs OR while the project is
  // playing — the track must not change under the audio the user is hearing.
  // Clip already refuses to wire these up in either case (its `isInert`); the
  // guards below are belt and braces. Transport and zoom stay live throughout.
  const isEditLocked = isLocked || isPlaying;

  // SNAP_THRESHOLD_PX expressed in seconds at the current zoom (START_OFFSET
  // cancels in the difference), so a dragged edge snaps to a neighbour within a
  // fixed on-screen distance regardless of zoom.
  const snapThresholdSec = useMemo(() => {
    const pxPerSec = secToPx(1) - secToPx(0);
    return pxPerSec > 0 ? SNAP_THRESHOLD_PX / pxPerSec : 0;
  }, [secToPx]);

  // Only record an undo/redo entry when the feature is on — same gate the block
  // editor uses for its timing edits (ContentBlock.handleChange{Start,End}Time).
  const undoRedoEnabled = getFeatureValue(
    FEATURE_KEYS.PROJECT_DUBBING_UNDO_REDO,
    {
      userId: user?.id,
      email: user?.email,
    },
  );

  const insertBlockEnabled = getFeatureValue(
    FEATURE_KEYS.PROJECT_DUBBING_INSERT_BLOCK,
    {
      userId: user?.id,
      email: user?.email,
    },
  );

  // Commit a dragged range once, on release. Timing-only change → the block
  // stays playable (getBlockStatus ignores timing); the clamp already kept it
  // valid, so validateBlockTimeChange here is just a guard against a bad edge.
  const commitRange = useCallback(
    (blockId, startSec, endSec) => {
      const block = blocks[blockId];
      if (!block) return;
      const index = blockIds.indexOf(blockId);
      const prevBlock = blocks[blockIds[index - 1]];
      const nextBlock = blocks[blockIds[index + 1]];
      const startTime = convertSecondsToSubtitleTime(startSec);
      const endTime = convertSecondsToSubtitleTime(endSec);
      const startOk =
        startTime === block.startTime ||
        validateBlockTimeChange({
          type: 'start',
          newTime: startTime,
          currentBlock: { ...block, endTime },
          prevBlock,
          nextBlock,
        }).valid;
      const endOk =
        endTime === block.endTime ||
        validateBlockTimeChange({
          type: 'end',
          newTime: endTime,
          currentBlock: { ...block, startTime },
          prevBlock,
          nextBlock,
        }).valid;
      if (!startOk || !endOk) return;
      const timingOptions = undoRedoEnabled
        ? {
            history: {
              commit: true,
              scope: HISTORY_SCOPE.DUBBING,
              label: HISTORY_LABEL.CHANGE_BLOCK_TIMING,
            },
          }
        : {};
      dispatch(
        actions.project.updateBlock(
          blockId,
          { startTime, endTime, blockStatus: block.blockStatus },
          timingOptions,
        ),
      );
      // CHANGE_BLOCK_TIMING is a DEFERRED history label (dubbingHistoryConfig):
      // the commit above only parks a pending entry so ContentBlock's rapid
      // time-input keystrokes coalesce, committed on blur. A drag release is
      // that same boundary — without this flush the entry stays pending and an
      // undo would just discard it, so the change would never enter the stack.
      if (undoRedoEnabled) {
        dispatch(actions.history.flush(HISTORY_SCOPE.DUBBING));
      }
    },
    [blocks, blockIds, dispatch, undoRedoEnabled],
  );

  // Press-and-drag a trim handle. `edge === 'end'` moves the right edge, `'start'`
  // the left; each clamp keeps the moving edge inside its neighbour (prev end /
  // next start) and ≥ CLIP_MIN_DURATION_SEC from the fixed edge.
  //
  // Identity-stable (see the note on handleBodyPointerDown below).
  const handleHandlePointerDown = useStableCallback((event, blockId, edge) => {
    if (isEditLocked) return;
    const index = blockIds.indexOf(blockId);
    const clip = clips[index];
    if (!clip) return;
    const { minStartSec, maxEndSec } = neighborBounds(
      clips,
      index,
      durationSec,
    );
    // Cap the slot at the audio's total length (timeline rule: a footage's
    // duration can't exceed its object's total duration). Undefined/0 when the
    // measurement hasn't settled → no cap, neighbour/min bounds still apply.
    const maxDurationSec =
      getMeasuredAudioDuration(blocks[blockId]?.audioLink) || Infinity;
    // Snap the moving edge onto a neighbour boundary when it comes close.
    const snapCandidates = [minStartSec, maxEndSec];
    let next = { startSec: clip.startSec, endSec: clip.endSec };
    beginDrag(event, {
      id: blockId,
      onMove: (deltaSec) => {
        if (edge === 'end') {
          next = clampTrimEnd({
            startSec: clip.startSec,
            rawEndSec: snapSec(
              clip.endSec + deltaSec,
              snapCandidates,
              snapThresholdSec,
            ),
            maxEndSec,
            minDurationSec: CLIP_MIN_DURATION_SEC,
            maxDurationSec,
          });
        } else {
          next = clampTrimStart({
            endSec: clip.endSec,
            rawStartSec: snapSec(
              clip.startSec + deltaSec,
              snapCandidates,
              snapThresholdSec,
            ),
            minStartSec,
            minDurationSec: CLIP_MIN_DURATION_SEC,
            maxDurationSec,
          });
        }
        setDraftRange({ id: blockId, ...next });
        // Walk the playhead with the edge being dragged, so it shows where the
        // new in-/out-point actually lands — the clamped, snapped value, not
        // wherever the cursor happens to be.
        onScrubTo?.(edge === 'end' ? next.endSec : next.startSec);
      },
      onEnd: () => {
        setDraftRange(null);
        commitRange(blockId, next.startSec, next.endSec);
        onScrubEnd?.(edge === 'end' ? next.endSec : next.startSec);
      },
    });
  });

  // Press-and-drag the clip body to move it, preserving its duration and staying
  // within its neighbours / the project bounds. A press with no real movement is
  // a select instead (the hook's click-vs-drag threshold) — the ONLY way a clip
  // selects, since a native onClick would also fire at the end of a real drag.
  //
  // Identity-stable rather than useCallback: the real dependencies (`blocks`,
  // `clips`, `commitRange`) change on every keystroke in ANY block, which would
  // hand every visible Clip a new prop and defeat React.memo(Clip) — re-running
  // emotion's style serialization and the waveform SVG for the whole viewport on
  // each character typed. Dropping the deps instead would capture stale clips and
  // drag from the wrong position. Only read at pointer-down, never in an effect's
  // dependency list, so the stable identity is safe.
  const handleBodyPointerDown = useStableCallback((event, blockId) => {
    if (isEditLocked) return;
    const index = blockIds.indexOf(blockId);
    const clip = clips[index];
    if (!clip) return;
    const { minStartSec, maxEndSec } = neighborBounds(
      clips,
      index,
      durationSec,
    );
    const snapCandidates = [minStartSec, maxEndSec];
    let next = { startSec: clip.startSec, endSec: clip.endSec };
    beginDrag(event, {
      id: blockId,
      onMove: (deltaSec) => {
        // Snap whichever edge lands closest to a neighbour boundary, then move
        // by that snapped delta so the clip butts flush instead of a px short.
        const rawStart = clip.startSec + deltaSec;
        const rawEnd = clip.endSec + deltaSec;
        const startShift =
          snapSec(rawStart, snapCandidates, snapThresholdSec) - rawStart;
        const endShift =
          snapSec(rawEnd, snapCandidates, snapThresholdSec) - rawEnd;
        const snapShift =
          startShift !== 0 &&
          (endShift === 0 || Math.abs(startShift) <= Math.abs(endShift))
            ? startShift
            : endShift;
        next = clampMove({
          startSec: clip.startSec,
          endSec: clip.endSec,
          deltaSec: deltaSec + snapShift,
          minStartSec,
          maxEndSec,
        });
        setDraftRange({ id: blockId, ...next });
      },
      onEnd: () => {
        setDraftRange(null);
        commitRange(blockId, next.startSec, next.endSec);
      },
      onClick: () => handleSelect(blockId),
    });
  });

  // Right-click a clip → context menu at the cursor. Right-click doesn't collide
  // with select (click) / trim / move (drag), and anchoring to the cursor keeps
  // it correct even on a clip trimmed too narrow to anchor to. Selecting the
  // block first mirrors the block list (its row highlights while the menu is up).
  const closeMenu = useCallback(() => setMenu(null), []);
  const handleContextMenu = useCallback(
    (event, blockId) => {
      event.preventDefault();
      // The menu only offers Delete, so it has nothing to show while locked
      // or playing.
      if (isEditLocked) return;
      highlightBlock(blockId);
      setMenu({
        position: { top: event.clientY, left: event.clientX },
        blockId,
      });
    },
    [highlightBlock, isEditLocked],
  );

  // Deleting is irreversible, so confirm first — the same dialog the block list
  // shows. The menu closes and the confirm dialog opens on the picked block.
  const handleDeleteBlock = useCallback(() => {
    if (!menu) return;
    setDeleteTargetId(menu.blockId);
    closeMenu();
  }, [menu, closeMenu]);

  const closeDeleteDialog = useCallback(() => setDeleteTargetId(null), []);

  const confirmDeleteBlock = useCallback(() => {
    if (!deleteTargetId) return;
    const deleteOptions = undoRedoEnabled
      ? {
          history: {
            commit: true,
            scope: HISTORY_SCOPE.DUBBING,
            label: HISTORY_LABEL.DELETE_BLOCK,
          },
        }
      : {};
    dispatch(actions.project.deleteBlock(deleteTargetId, deleteOptions));
    dispatch(actions.project.unselectBlocks([deleteTargetId]));
    setDeleteTargetId(null);
  }, [deleteTargetId, undoRedoEnabled, dispatch]);

  // Matches the block list's copy: "all blocks" when it's the last one, else 1.
  const deleteCountLabel = blockIds.length === 1 ? t('allBlocks') : 1;

  // One insert slot per gap, clamped to the viewport's real px edges (read here,
  // not the whole-second-rounded visibleRange). Skipped while locked/playing.
  // Leading gap → a beforeId slot; gaps between clips → afterId slots.
  const insertSlots = useMemo(() => {
    if (!insertBlockEnabled || isEditLocked || draftRange) return [];
    const viewport = viewportRef.current;
    const windowLeftPx = viewport
      ? viewport.scrollLeft
      : secToPx(visibleRange.fromSec);
    const windowRightPx = viewport
      ? viewport.scrollLeft + viewport.clientWidth
      : secToPx(visibleRange.toSec);
    const slotIn = (gapStart, gapEnd) => {
      const left = Math.max(secToPx(gapStart), windowLeftPx);
      const right = Math.min(secToPx(gapEnd), windowRightPx);
      const width = right - left;
      return width >= INSERT_SLOT_MIN_PX ? { left, width } : null;
    };

    const seed = [];
    const first = clips[0];
    if (first && first.startSec > 0) {
      const slot = slotIn(0, first.startSec);
      if (slot) seed.push({ beforeId: first.id, ...slot });
    }
    return clips.reduce((slots, clip, index) => {
      const nextClip = clips[index + 1];
      if (!nextClip || nextClip.startSec <= clip.endSec) return slots;
      const slot = slotIn(clip.endSec, nextClip.startSec);
      if (slot) slots.push({ afterId: clip.id, ...slot });
      return slots;
    }, seed);
  }, [
    insertBlockEnabled,
    clips,
    visibleRange,
    isEditLocked,
    draftRange,
    secToPx,
    viewportRef,
  ]);

  return (
    <StyledLane>
      {visibleClips.map((clip) => (
        <Clip
          key={clip.id}
          id={clip.id}
          startSec={clip.startSec}
          endSec={clip.endSec}
          text={clip.text}
          variant={visibleVariants[clip.id]}
          selected={clip.id === currentBlockId}
          hasAudio={audioIds.has(clip.id)}
          waveform={blockWaveforms.get(clip.id)}
          draftRange={
            draftRange && draftRange.id === clip.id
              ? { startSec: draftRange.startSec, endSec: draftRange.endSec }
              : null
          }
          onHandlePointerDown={handleHandlePointerDown}
          onBodyPointerDown={handleBodyPointerDown}
          onContextMenu={handleContextMenu}
          loading={blocks[clip.id]?.status === CONVERTING_STATUS.IN_PROGRESS}
          isLocked={isLocked}
          isPlaying={isPlaying}
        />
      ))}
      {!isAddBlockDisabled &&
        insertSlots.map(({ afterId, beforeId, left, width }) => (
          <InsertButton
            key={beforeId ? `insert-before-${beforeId}` : `insert-${afterId}`}
            title={t('addMoreBlock')}
            left={left}
            width={width}
            onClick={() =>
              beforeId
                ? onInsertBlock?.(beforeId, { before: true })
                : onInsertBlock?.(afterId)
            }
          />
        ))}
      {draftRange && (
        <StyledDragTooltip
          left={secToPx((draftRange.startSec + draftRange.endSec) / 2)}
        >
          {`${convertSecondsToSubtitleTime(
            draftRange.startSec,
          )} → ${convertSecondsToSubtitleTime(draftRange.endSec)}`}
        </StyledDragTooltip>
      )}
      {skeletonLeft !== null && <ClipSkeleton left={skeletonLeft} />}
      <StyledAppendWrap ref={appendRef} data-timeline-append left={appendLeft}>
        <AppendButton
          label={t('addMoreBlock')}
          onClick={onAddBlock}
          disabled={isAddBlockDisabled}
        />
      </StyledAppendWrap>
      <ClipMenu
        open={Boolean(menu)}
        anchorPosition={menu?.position}
        onClose={closeMenu}
        items={
          menu
            ? [
                {
                  key: 'delete',
                  label: t('delete'),
                  icon: <DeleteOutlineRoundedIcon fontSize="small" />,
                  onClick: handleDeleteBlock,
                },
              ]
            : []
        }
      />
      <CommonDialog
        width="512px"
        title={t('confirmDelete')}
        fullWidth
        open={Boolean(deleteTargetId)}
        onClose={closeDeleteDialog}
        contentComponent={
          <Typography
            variant="body-16-regular"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: t('areYouSureWantToDeleteBlock', {
                value: deleteCountLabel,
              }),
            }}
          />
        }
        actionComponent={
          <>
            <CommonButton
              customStyle={{
                padding: '8px 24px',
                borderRadius: '8px',
                height: '40px',
              }}
              variant="outlineSmall"
              textProps={{
                variantText: 'body-14-semibold',
                color: COLOR.darkBlue,
              }}
              onClick={closeDeleteDialog}
            >
              {t('cancel')}
            </CommonButton>
            <CommonButton
              customStyle={{
                padding: '8px 24px',
                backgroundColor: COLOR.error,
                borderRadius: '8px',
                height: '40px',
              }}
              textProps={{
                variantText: 'body-14-semibold',
                color: COLOR.white,
              }}
              color="error"
              variant="contained"
              onClick={confirmDeleteBlock}
            >
              {t('delete')}
            </CommonButton>
          </>
        }
      />
    </StyledLane>
  );
};

// Memoized so a parent re-render (seek, pause) with unchanged props doesn't
// re-render it. It still re-renders on its own currentBlockId (clip highlight).
export default React.memo(VoiceoverTrack);
